import { BlockTitle, List, ListItem } from 'framework7-react';
import { SettingIcon } from '@/components/SettingIcon';
import { useT } from '@/i18n';
import type { SettingsHealthCode, SettingsHealthIssue } from '../hooks/useSettingsHealth';

// F7 glyph per gap — distinct so the owner can scan the list at a glance. Amber
// throughout signals "warning, not error": nothing is broken beyond repair, it
// just needs finishing.
const ICON: Record<SettingsHealthCode, string> = {
  location: 'placemark',
  openingHours: 'clock',
  deliveryPricing: 'bag',
  deliveryTime: 'timer',
  paymentMethod: 'creditcard',
  menu: 'square_list',
  menuNotLive: 'square_list',
};

const WARNING = '#f59e0b';

interface Props {
  issues: SettingsHealthIssue[];
  // Opens the fix page (and its parent first, when set), on whichever settings
  // router is active — so a tapped problem lands on the right page with a Back
  // button that returns somewhere sensible.
  onOpen: (issue: SettingsHealthIssue) => void;
}

/**
 * Top-of-Settings alert listing unfinished setup that silently breaks the
 * storefront (no location, no hours, no delivery pricing). Each row deep-links
 * to the page that fixes it. Renders nothing when everything is configured.
 */
export function SettingsHealthBanner({ issues, onOpen }: Props) {
  const t = useT();
  if (issues.length === 0) return null;

  const copy = t.settingsHealth;

  return (
    <div className="settings-health">
      <BlockTitle className="settings-health-title">
        <i className="f7-icons settings-health-title-icon">exclamationmark_triangle_fill</i>
        {copy.title}
      </BlockTitle>
      <List
        strongIos
        strongMd
        insetIos
        insetMd
        dividersIos
        dividersMd
        className="settings-health-list"
      >
        {issues.map((issue) => (
          <ListItem
            key={issue.code}
            link="#"
            onClick={() => onOpen(issue)}
            title={copy[issue.code].title}
            text={copy[issue.code].description}
          >
            <div slot="media">
              <SettingIcon icon={ICON[issue.code]} color={WARNING} />
            </div>
          </ListItem>
        ))}
      </List>
    </div>
  );
}
